import React, { useState } from "react";
import "./FaqPage.css";

const FAQ_ITEMS = [
  {
    q: "Why does PaceWerks not tell me what pace I should be training at?",
    a: "Great question. While we're called PaceWerks, our plans prioritize perceived effort (PRE/RPE). Pace is not one-size-fits-all, but effort scales with your fitness. Every runner is different. You might be training in heat, on hills, after a tough workday, or on fresh legs. Prescribing exact paces can lead to burnout, injury, or discouragement, especially if you're having an off day. Using RPE-based training helps you listen to your body, train smarter, and adapt in real time. We chose the name PaceWerks because we believe good pacing starts with effort awareness, not just your watch.",
  },
  {
    q: "Is this really free? What's the catch?",
    a: "Yes, it's completely free to generate a custom training plan. No credit card. No email required. Our goal is to make high-quality training accessible to all runners, and over time, we'll offer optional paid upgrades for those who want more tools or coaching.",
  },
  {
    q: "Can I change my plan after generating it?",
    a: "Yes! The form is embedded right above your training plan. You can update your race date or training intensity at any time and instantly regenerate your plan, no need to start from scratch.",
  },
  {
    q: "What do the training intensities mean?",
    a: "We let you choose how hard you want to train. Easy: Minimal structure, lower mileage, and no intense workouts, perfect for beginners or casual runners. Moderate: Balanced plan with some workouts and weekly progression. Challenging: Structured plan with intervals, long runs, and a focus on improvement. Intense: Peak performance focus, higher mileage, and harder workouts, great for chasing PRs.",
  },
  {
    q: "Will this work for beginners?",
    a: "Absolutely. Whether you're running your first 5K or your tenth marathon, the plan adjusts based on your experience, goals, and weekly availability.",
  },
  {
    q: "Can I sync this with my calendar or running watch?",
    a: "Not yet, but it's on our roadmap. For now, you can export your plan as a printable PDF. We're working on future integrations with Google Calendar, iPhone calendar, and platforms like Strava or Garmin.",
  },
  {
    q: "I'm training for a less common race. Can you still build a plan?",
    a: "Not yet, but soon. We support all major distances and are expanding to include ultras and custom distances in the near future. If your distance isn't listed, check back soon or send us a note so we can prioritize it.",
  },
  {
    q: "How can I support PaceWerks?",
    a: "We'd love that! You can share the site with your running friends, post your plan on social media and tag us, or leave feedback and suggestions so we can keep improving.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqSchemaJson = JSON.stringify(faqSchema);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: faqSchemaJson }}
      />
      <div className="faq-page">
        <div className="faq-inner">
        <h1 className="faq-title">FAQ</h1>
        <div className="faq-stack">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <article key={i} className="faq-card">
                <button
                  type="button"
                  className="faq-trigger"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  id={`faq-trigger-${i}`}
                  onClick={() =>
                    setOpenIndex((prev) => (prev === i ? null : i))
                  }
                >
                  <span className="faq-question">{item.q}</span>
                  <span
                    className={`faq-icon${isOpen ? " faq-icon--open" : ""}`}
                    aria-hidden
                  >
                    +
                  </span>
                </button>
                <div
                  id={`faq-panel-${i}`}
                  className={`faq-panel${isOpen ? " faq-panel--open" : ""}`}
                  role="region"
                  aria-labelledby={`faq-trigger-${i}`}
                >
                  <div className="faq-panel-inner">
                    <p className="faq-answer">{item.a}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        </div>
      </div>
    </>
  );
}
